<template>
	<section
		class="card-grid"
		:class="{ 'card-grid--mobile-wrap': mobileColumns > 1 }"
		:style="sectionVars"
	>
		<div class="container">
			<h2 v-if="title" class="card-grid__title headline-l">{{ title }}</h2>

			<div class="card-grid__grid">
				<div v-for="(item, i) in items" :key="i" class="card-grid__card">
					<div class="card-grid__header">
						<img
							v-if="item.icon && iconPosition === 'start'"
							:src="item.icon"
							class="card-grid__icon"
							alt=""
						/>
						<span class="card-grid__label">{{ item.label }}</span>
						<img
							v-if="item.icon && iconPosition === 'end'"
							:src="item.icon"
							class="card-grid__icon"
							alt=""
						/>
					</div>
					<div
						v-if="item.title || item.body || item.rows"
						class="card-grid__body"
					>
						<h3 v-if="item.title" class="card-grid__card-title">
							{{ item.title }}
						</h3>

						<div v-if="item.body" v-html="item.body"></div>

						<ul v-if="item.rows" class="card-grid__rows">
							<li v-for="(row, r) in item.rows" :key="r">
								<span class="card-grid__row-label">{{ row.label }}</span>
								<a
									v-if="row.link"
									class="card-grid__row-value is-link"
									:href="row.link"
									target="_blank"
									rel="noopener"
									>{{ row.value }}</a
								>
								<span v-else class="card-grid__row-value">{{ row.value }}</span>
							</li>
						</ul>
					</div>

					<img
						v-if="separatorIcon && i < items.length - 1"
						:src="separatorIcon"
						class="card-grid__separator"
						alt=""
					/>
				</div>
			</div>

			<div v-if="$slots.footer" class="card-grid__footer">
				<slot name="footer" />
			</div>

			<p v-if="note" class="card-grid__note">
				<img v-if="noteIcon" :src="noteIcon" class="card-grid__note-icon" alt="" />
				<span v-html="note"></span>
			</p>
		</div>
	</section>
</template>

<script>
export default {
	name: 'CardGridSection',
	props: {
		title: { type: String, default: '' },
		// [{ label, icon, title, body, rows }] -- `label` is the header line,
		// `title` an uppercase heading under it, `body` free text (v-html) and
		// `rows` an optional [{ label, value }] list. Only `label` is required.
		items: { type: Array, default: () => [] },
		note: { type: String, default: '' },
		noteIcon: { type: String, default: '' },
		// artwork behind the whole section
		backgroundImage: { type: String, default: '' },
		// 'end' pushes the icon to the far side of the header, as the step cards do
		iconPosition: {
			type: String,
			default: 'start',
			validator: (v) => ['start', 'end'].includes(v),
		},
		headerBorderBottom: { type: String, default: 'none' },
		cardBackground: { type: String, default: 'transparent' },
		cardMinHeight: { type: String, default: 'auto' },
		iconSize: { type: String, default: '20px' },
		gap: { type: String, default: '40px 30px' },
		headerMinHeight: { type: String, default: '48px' },
		bodyPadding: { type: String, default: '16px' },
		// chevron dropped between cards (the step flow)
		separatorIcon: { type: String, default: '' },
		columns: { type: Number, default: 2 },
		// lets a grid sit flush against an adjoining section of the same colour
		paddingY: { type: Number, default: 90 },
		// hairline separating this section from the one above it
		borderTop: { type: String, default: 'none' },
		backgroundColor: { type: String, default: '#E8E8FF' },
		titleColor: { type: String, default: '#050535' },
		textColor: { type: String, default: '#262A52' },
		headerBackground: { type: String, default: '#4656FF' },
		headerTextColor: { type: String, default: '#FFFFFF' },
		cardBorderColor: { type: String, default: 'transparent' },
		// i18n keeps natural casing; sections that render caps ask for it here
		bodyTextTransform: { type: String, default: 'none' },
		labelTextTransform: { type: String, default: 'uppercase' },
		cardBodyBackground: { type: String, default: 'transparent' },
		// 'row' puts the icon beside the label, 'column' stacks it above
		headerDirection: {
			type: String,
			default: 'row',
			validator: (v) => ['row', 'column'].includes(v),
		},
		align: {
			type: String,
			default: 'left',
			validator: (v) => ['left', 'center'].includes(v),
		},
		// the mockup centres every card's copy once stacked, bar the supply
		// panels, whose label/value rows stay flush left and right
		mobileAlign: {
			type: String,
			default: 'center',
			validator: (v) => ['left', 'center'].includes(v),
		},
		// more than one column below $breakpoint-sm: the cards wrap and the short
		// last row is centred, which a grid cannot do
		mobileColumns: { type: Number, default: 1 },
	},
	components: {},
	data: () => ({}),
	created() {},
	computed: {
		sectionVars() {
			return {
				'--bg': this.backgroundColor,
				'--title-color': this.titleColor,
				'--text-color': this.textColor,
				'--header-bg': this.headerBackground,
				'--header-text': this.headerTextColor,
				'--card-border': this.cardBorderColor,
				'--align': this.align,
				'--mobile-align': this.mobileAlign,
				'--mobile-justify':
					this.mobileAlign === 'center' ? 'center' : 'flex-start',
				'--mobile-columns': this.mobileColumns,
				'--justify': this.align === 'center' ? 'center' : 'flex-start',
				'--columns': this.columns,
				'--padding-y': `${this.paddingY}px`,
				'--border-top': this.borderTop,
				'--body-transform': this.bodyTextTransform,
				'--label-transform': this.labelTextTransform,
				'--card-body-bg': this.cardBodyBackground,
				'--header-direction': this.headerDirection,
				'--header-border': this.headerBorderBottom,
				'--card-bg': this.cardBackground,
				'--card-min-height': this.cardMinHeight,
				'--icon-size': this.iconSize,
				'--gap': this.gap,
				'--header-min-height': this.headerMinHeight,
				'--body-padding': this.bodyPadding,
				'--bg-image': this.backgroundImage
					? `url(${this.backgroundImage})`
					: 'none',
				'--header-justify':
					this.iconPosition === 'end' ? 'space-between' : this.align === 'center' ? 'center' : 'flex-start',
				'--mobile-header-justify':
					this.iconPosition === 'end'
						? 'space-between'
						: this.mobileAlign === 'center'
						? 'center'
						: 'flex-start',
			};
		},
	},
	methods: {},
};
</script>

<style lang="scss" scoped>
.card-grid {
	background-color: var(--bg);
	background-image: var(--bg-image);
	background-repeat: no-repeat;
	background-position: center;
	background-size: cover;
	border-top: var(--border-top);
	padding: var(--padding-y) 0;

	@media (max-width: $breakpoint-sm) {
		padding: 50px 0;
	}
}

.card-grid__title {
	color: var(--title-color);
	text-transform: uppercase;
	margin-bottom: 40px;

	// every section heading is centred once stacked, as the rest of the site does
	@media (max-width: $breakpoint-sm) {
		text-align: center;
		margin-bottom: 30px;
	}
}

.card-grid__grid {
	display: grid;
	grid-template-columns: repeat(var(--columns), 1fr);
	gap: var(--gap);

	@media (max-width: $breakpoint-md) {
		grid-template-columns: repeat(2, 1fr);
		gap: 30px 20px;
	}

	@media (max-width: $breakpoint-sm) {
		grid-template-columns: 1fr;
		gap: 24px;
	}
}

// mobileColumns > 1: flex-wrap instead of a grid so the short final row centres.
// Applied from the tablet breakpoint down -- the generic two-column fallback
// leaves a lone card stranded on the last row.
.card-grid--mobile-wrap .card-grid__grid {
	@media (max-width: $breakpoint-md) {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 12px;

		> * {
			flex: 0 1 calc(100% / var(--mobile-columns) - 12px);
			// height: 100% pins the card to the container instead of its own row,
			// so the cards in a line end up ragged; let the flex stretch do it
			height: auto;
		}
	}

	// three to a row on a phone leaves each card barely 100px wide -- at the
	// full size the labels broke across four lines and the cards drifted apart
	@media (max-width: $breakpoint-sm) {
		.card-grid__label,
		.card-grid__body {
			font-size: 12px;
			line-height: 12px;
		}
	}
}

.card-grid__card {
	position: relative;
	display: flex;
	flex-direction: column;
	border: 1px solid var(--card-border);
	background: var(--card-bg);
	min-height: var(--card-min-height);
	height: 100%;
}

// sits in the gap to the right of every card but the last
.card-grid__separator {
	position: absolute;
	top: 50%;
	right: -12px;
	width: 12px;
	transform: translateY(-50%);

	// stacked cards chain downwards, so the chevron turns with them
	@media (max-width: $breakpoint-sm) {
		top: auto;
		right: auto;
		bottom: -18px;
		left: 50%;
		transform: translateX(-50%) rotate(90deg);
	}
}

// Between the tablet and desktop breakpoints the grid re-flows to two columns,
// so the chevron on a row's last card points out of the grid -- at the gutter,
// not at the card that starts the next row. Only the in-row ones stay.
@media (min-width: 768px) and (max-width: $breakpoint-md) {
	.card-grid__card:nth-child(2n) .card-grid__separator {
		display: none;
	}
}

.card-grid__header {
	display: flex;
	flex-direction: var(--header-direction);
	align-items: center;
	justify-content: var(--header-justify);
	gap: 10px;
	background: var(--header-bg);
	border-bottom: var(--header-border);
	padding: 12px 16px;
	min-height: var(--header-min-height);

	@media (max-width: $breakpoint-sm) {
		justify-content: var(--mobile-header-justify);
	}
}

.card-grid__icon {
	width: var(--icon-size);
	height: var(--icon-size);
	flex-shrink: 0;
	object-fit: contain;
}

.card-grid__label {
	color: var(--header-text);
	font-family: 'HKNova-Bold', sans-serif;
	font-size: $text-m;
	line-height: 18px;
	text-transform: var(--label-transform);
}

.card-grid__body {
	color: var(--text-color);
	background: var(--card-body-bg);
	font-size: $text-l;
	line-height: 24px;
	padding: var(--body-padding);
	flex: 1 1 auto;
	text-align: var(--align);
	text-transform: var(--body-transform);

	@media (max-width: $breakpoint-sm) {
		text-align: var(--mobile-align);
	}

	&::v-deep {
		p {
			margin-bottom: 12px;

			&:last-child {
				margin-bottom: 0;
			}
		}

		b,
		strong {
			font-family: 'HKNova-Bold', sans-serif;
		}
	}
}

.card-grid__card-title {
	font-family: 'HKNova-Bold', sans-serif;
	font-size: $text-l;
	line-height: 22px;
	text-transform: uppercase;
	margin-bottom: 10px;
}

.card-grid__rows {
	list-style: none;
	margin: 0;
	padding: 0;

	li {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 16px;
		padding: 8px 0;
		text-align: left;
	}
}

.card-grid__row-value {
	font-family: 'HKNova-Bold', sans-serif;
	white-space: nowrap;

	// a row value can carry a link; global.scss would otherwise recolour it
	&.is-link {
		color: inherit;
		text-decoration: underline;

		&:hover {
			color: inherit;
		}
	}
}

.card-grid__footer {
	margin-top: 40px;
}

.card-grid__note {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--text-color);
	font-size: $text-s;
	line-height: 20px;
	margin-top: 32px;

	@media (max-width: $breakpoint-sm) {
		justify-content: center;
		text-align: center;
	}
}

.card-grid__note-icon {
	width: 20px;
	height: 20px;
	flex-shrink: 0;
}
</style>
